import React, { useLayoutEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-final-form';
import axios from 'axios';
import { useQuery, useMutation } from '@tanstack/react-query';

import Modal, { Footer } from 'common/Modal';
import { FileField, CheckboxField } from 'common/Forms';
import { composeValidators, fileSizeLimiter, required } from 'common/Forms/Validators';
import Tooltip from 'common/Tooltip';
import simpleAudioButtonPlayerImage from 'images/audioButtonTooltip/simple-audio-button-image.png';
import htmlAudioPlayerImage from 'images/audioButtonTooltip/html5-audio-player-image.png';
import { addFileToInput, slideObjectPropTypes } from '@/components/admin/SlideShow/Utils';
import { plato_api_slide_sound_clip_path, plato_api_slide_sound_clips_path } from '@/modules/routes';
import useSlideShowStore from '@/components/admin/SlideShow/stores/useSlideShowStore';
import { insertIntoEditor } from '../FroalaListeners';
import styles from './CustomAudioModal.module.scss';

const AudioForm = ({
  $audioButtonToEdit, editorInstance, modalManager, slideObject, slideSoundClipId, useFullPlayerInitialValue, userType
}) => {
  const { data, isLoading: audioFileLoading } = useQuery({
    queryFn: () => (
      !slideSoundClipId ? null : axios
        .get(plato_api_slide_sound_clip_path(slideSoundClipId))
        .then(res => res.data)
        .catch(e => console.log(e))
    ),
    queryKey: [slideSoundClipId]
  });
  const [audioFile, setAudioFile] = useState(null);
  const [audioFileEs, setAudioFileEs] = useState(null);
  const setHasUnsavedChanges = useSlideShowStore(state => state.setHasUnsavedChanges);

  useLayoutEffect(() => {
    // pre-populate the input with an audio file if given
    if (slideSoundClipId && data && !audioFileLoading) {
      addFileToInput({
        callback: setAudioFile,
        filename: data.slideSoundClipFileName,
        inputElementId: 'audioFile_field',
        url: data.s3Url
      });
      addFileToInput({
        callback: setAudioFileEs,
        filename: data.slideSoundClipEsFileName,
        inputElementId: 'audioFileEs_field',
        url: data.s3UrlEs
      });
    }
  }, [data]);

  const audioTag = (slideSoundClip, useFullPlayer) => {
    if (useFullPlayer) {
      return (
        <span className="full-player-span" contentEditable="false">
          <audio
            controls
            data-sound-clip-id={slideSoundClip.id}
            data-src-es={slideSoundClip.s3UrlEs}
            src={slideSoundClip.s3Url}
          >
            Your browser does not support the audio element.
          </audio>
        </span>
      );
    }

    return (
      <span
        className="simple-audio-player btn btn--md btn--outline-purple"
        contentEditable={false}
        data-command="play"
        data-sound-clip-id={slideSoundClip.id}
        data-src={slideSoundClip.s3Url}
        data-src-es={slideSoundClip.s3UrlEs}
        role="button"
        tabIndex={0}
      >
        <i aria-hidden className="fa fa-play" contentEditable="false" />
      </span>
    );
  };

  const modifyExistingButton = (newSlideSoundClip, useFullPlayer) => {
    const srcAttr = useFullPlayer ? 'src' : 'data-src';
    $audioButtonToEdit.attr('data-sound-clip-id', newSlideSoundClip.id).attr(srcAttr, newSlideSoundClip.s3Url);

    if (newSlideSoundClip.s3UrlEs) {
      $audioButtonToEdit.attr('data-src-es', newSlideSoundClip.s3UrlEs);
    }

    setHasUnsavedChanges(true);
    editorInstance.undo.saveStep();
  };

  const { isLoading: loading, mutate: createSlideSoundClip } = useMutation({
    mutationFn: formData => (
      axios.post(plato_api_slide_sound_clips_path(), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    ),
    onSuccess: (res, formData) => {
      const newSlideSoundClip = res.data;

      // if we're editing an existing audio button
      if ($audioButtonToEdit) {
        if (formData.use_full_player !== useFullPlayerInitialValue) {
          // if we're changing it to use the full player, delete the DOM node and insert an html5 audio tag
          editorInstance.selection.setBefore($audioButtonToEdit[0]);
          $audioButtonToEdit.remove();

          insertIntoEditor(editorInstance, audioTag(newSlideSoundClip, formData.use_full_player));
        }
        else {
          // otherwise just modify the data-sound-clip-id and data-src
          modifyExistingButton(newSlideSoundClip, formData.use_full_player);
        }
      }
      else {
        insertIntoEditor(editorInstance, audioTag(newSlideSoundClip, formData.use_full_player));
      }

      modalManager.close();
    },
    onError: e => console.log(e),
  });

  const audioFileToUpload = (newAudioFile, existingAudioFile) => {
    if (newAudioFile && newAudioFile instanceof File) return newAudioFile;
    if (existingAudioFile) return existingAudioFile;

    return null;
  };

  const submitForm = (formValues) => {
    const variables = {
      audio_file: audioFileToUpload(formValues.audioFile, audioFile),
      audio_file_es: audioFileToUpload(formValues.audioFileEs, audioFileEs),
      skip_transcription: formValues.skip_transcription || false,
      slide_object_id: slideObject.id,
      use_full_player: formValues.use_full_player || false,
    };

    createSlideSoundClip(variables);
  };

  const fileField = () => {
    if (audioFileLoading) return ('Loading Audio File');

    if (userType === 'Teacher') {
      return (
        <FileField
          accept=".mp3"
          fileTypeText="File Requirement: .MP3 and less than 20MB"
          label="Audio File"
          name="audioFile"
          required={!audioFile}
          validate={composeValidators(required, fileSizeLimiter(20, 'File'))}
        />
      );
    }

    return (
      <FileField
        accept="audio/mp3, audio/mp4, audio/mpeg"
        label="Audio File"
        name="audioFile"
        required={!audioFile}
        validate={required}
      />
    );
  };

  const tooltipContent = () => (
    <div className={styles.tooltip}>
      <p>Default player:</p>
      <img alt="simplified play button" className={styles.simpleAudioButtonImage} src={simpleAudioButtonPlayerImage} />
      <p className="mt15">Full player:</p>
      <img alt="full sized audio player" className={styles.htmlAudioImage} src={htmlAudioPlayerImage} />
    </div>
  );

  const label = () => (
    <span>
      Use full player
      {' '}
      <Tooltip
        allowHTML
        content={tooltipContent()}
        interactive={false}
        placement="right"
        size="medium"
        theme="white"
      >
        <i className="fa fa-question-circle" />
      </Tooltip>
    </span>
  );

  return (
    <Form
      initialValues={{
        audioFile: audioFile ? audioFile.name : null,
        audioFileEs: audioFileEs ? audioFileEs.name : null,
        skip_transcription: data?.skipTranscription,
        use_full_player: useFullPlayerInitialValue,
      }}
      render={({
        form,
        handleSubmit,
        invalid,
        pristine
      }) => (
        <form onSubmit={handleSubmit}>
          {fileField()}
          {userType !== 'Teacher' && (
            <FileField
              accept="audio/mp3, audio/mp4, audio/mpeg"
              label="Audio File (Spanish)"
              name="audioFileEs"
              value={audioFileEs ? audioFileEs.name : null}
            />
          )}
          <CheckboxField
            fieldClass="mr5"
            label={label()}
            labelPos="right"
            name="use_full_player"
          />
          {userType !== 'Teacher' && (
            <CheckboxField
              fieldClass="mr5"
              label="Skip transcription"
              labelPos="right"
              name="skip_transcription"
            />
          )}
          <Footer
            primaryButtonDisabled={invalid || pristine || audioFileLoading}
            primaryButtonText="Submit"
            secondaryButtonCallback={modalManager.close}
            submitting={loading}
          />
        </form>
      )}
      onSubmit={submitForm}
    />
  );
};

AudioForm.propTypes = {
  $audioButtonToEdit: PropTypes.object,
  editorInstance: PropTypes.object,
  modalManager: PropTypes.shape({
    close: PropTypes.func.isRequired,
    isOpen: PropTypes.bool.isRequired,
    open: PropTypes.func.isRequired
  }).isRequired,
  slideObject: slideObjectPropTypes.isRequired,
  slideSoundClipId: PropTypes.string,
  useFullPlayerInitialValue: PropTypes.bool,
  userType: PropTypes.string.isRequired,
};

AudioForm.defaultProps = {
  $audioButtonToEdit: null,
  editorInstance: {},
  slideSoundClipId: '',
  useFullPlayerInitialValue: false,
};

const CustomAudioModal = ({
  $audioButtonToEdit, editorInstance, modalManager, setAudioButtonToEdit,
  setSlideSoundClipId, slideObject, slideSoundClipId, userType
}) => {
  const closeModal = () => {
    setAudioButtonToEdit(null);
    setSlideSoundClipId('');
    modalManager.close();
  };

  const useFullPlayer = $audioButtonToEdit && $audioButtonToEdit.prop('tagName') === 'AUDIO';

  return (
    <Modal
      closeModal={closeModal}
      headerText={`${slideSoundClipId ? 'Edit' : 'Add'} Audio`}
      isOpen={modalManager.isOpen}
    >
      <AudioForm
        $audioButtonToEdit={$audioButtonToEdit}
        editorInstance={editorInstance}
        modalManager={modalManager}
        slideObject={slideObject}
        slideSoundClipId={slideSoundClipId}
        useFullPlayerInitialValue={useFullPlayer}
        userType={userType}
      />
    </Modal>
  );
};

CustomAudioModal.propTypes = {
  $audioButtonToEdit: PropTypes.object,
  editorInstance: PropTypes.object,
  modalManager: PropTypes.shape({
    close: PropTypes.func.isRequired,
    isOpen: PropTypes.bool.isRequired,
    open: PropTypes.func.isRequired
  }).isRequired,
  setAudioButtonToEdit: PropTypes.func.isRequired,
  setSlideSoundClipId: PropTypes.func.isRequired,
  slideObject: slideObjectPropTypes.isRequired,
  slideSoundClipId: PropTypes.string,
  userType: PropTypes.string.isRequired,
};

CustomAudioModal.defaultProps = {
  $audioButtonToEdit: null,
  editorInstance: {},
  slideSoundClipId: '',
};

export default CustomAudioModal;
